// src/api/vendors.ts
//
// Vendor API — mirrors clientsApi exactly, just hits /vendors instead of
// /clients. Backend dispatches both surfaces from the same router factory,
// discriminated by the `entity_type` column on client_details.
import api from "./api";
import { downloadImportTemplate } from "./clients";

export interface Vendor {
  id: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  gstin: string | null;
  pan: string | null;
  entity_type: "vendor";
  user_id: string;
  branchID: string | null;
  branches?: Array<{
    id?: string;
    branchName: string;
    gstin?: string | null;
    address1?: string | null;
    address2?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    linked_branch_id?: string | null;
  }>;
  // Cross-org link metadata. When `is_linked` is true the row mirrors the
  // basic_details profile of another Teamlio org and is read-only here.
  linked_headOfficeID?: string | null;
  linked_user_id?: string | null;
  is_linked?: boolean;
  // Messenger conversation id of the shared workspace group between the two
  // orgs. Populated for linked rows once the messenger webhook succeeds; the
  // "Open workspace" button is hidden while null (e.g. webhook queued).
  cross_org_group_id?: string | null;
}

export interface VendorCreatePayload {
  clientName: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  branchDetails?: any[];
}

export interface VendorUpdatePayload {
  clientName?: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  branchDetails?: any[];
}

export const vendorsApi = {
  list: (params?: { search?: string; page?: number; page_size?: number }) =>
    api.get("/vendors", { params }),

  get: (vendorId: string) =>
    api.get(`/vendors/${vendorId}`),

  create: (payload: VendorCreatePayload) =>
    api.post("/vendors", payload),

  update: (vendorId: string, payload: VendorUpdatePayload) =>
    api.patch(`/vendors/${vendorId}`, payload),

  // Compose-and-send an email to the vendor from the detail view. Sent from
  // Teamlio's default address (noreply); the sender is CC'd server-side.
  sendEmail: (vendorId: string, payload: { subject: string; body: string }) =>
    api.post(`/vendors/${vendorId}/send-email`, payload),

  delete: (vendorId: string) =>
    api.delete(`/vendors/${vendorId}`),

  settings: () =>
    api.get('/get_startup_khata'),

  importVendors: (payload: any[]) =>
    api.post('/vendors/import_clients', payload),

  importExcel: (formData: FormData) =>
    api.post("/vendors/import_client_excel", formData),

  downloadImportTemplate: () =>
    downloadImportTemplate("vendors", "vendor_import_template.xlsx"),
};
